var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { boundMethod } from 'autobind-decorator';
import { Container } from 'pixi.js';
import { HighlightType } from 'flow-builder/types/node';
import { createTextureSpriteMap, setHighlightSpriteVisibility, } from '../utils/highlightTextureMap';
import { styles as nodeStyles } from './nodeStyles';
import { DragListener } from './nodeUtils';
export class ActionNode {
    constructor(props) {
        this.name = 'ActionNode';
        this.isDragging = false;
        this.dragListener = null;
        this.props = props;
        const { actionNodeTextureMap } = props;
        const container = new Container();
        const nodeHighlightTypes = createTextureSpriteMap(actionNodeTextureMap, 'Node Highlight');
        this.nodeHighlightTypes = nodeHighlightTypes;
        Object.values(this.nodeHighlightTypes).forEach((sprite) => {
            sprite.position.set(nodeStyles.base.width / 2, 0);
            container.addChild(sprite);
        });
        container.interactive = true;
        container.buttonMode = true;
        this.ref = container;
        this.addClickListener();
        this.addDragListeners();
        this.drawNodePosition();
        this.drawNodeHighlight();
    }
    get dragListenerProps() {
        const { node, editorContext } = this.props;
        const container = this.ref;
        return {
            node,
            container,
            editorContext,
            onDragStart: this.onDragStart,
            onDragMove: this.onDragMove,
            onDragEnd: this.onDragEnd,
        };
    }
    addClickListener() {
        const container = this.ref;
        container.on('click', this.onClick);
    }
    onClick() {
        const { node, editorContext: { handlers: { node: { onEdit }, }, }, } = this.props;
        if (this.isDragging) {
            return;
        }
        onEdit(node);
    }
    addDragListeners() {
        const props = this.dragListenerProps;
        if (!this.dragListener) {
            const dragListener = new DragListener(props);
            dragListener.addListeners();
            this.dragListener = dragListener;
        }
        else {
            this.dragListener.props = props;
        }
    }
    onDragStart() {
        const { node, editorContext } = this.props;
        const { state: { setAttachState }, } = editorContext;
        this.isDragging = true;
        setAttachState((state) => (Object.assign(Object.assign({}, state), { actionNodeId: node.id, isDragging: true })));
    }
    onDragMove() {
        const { editorContext: { handlers: { action: { onMove }, }, }, } = this.props;
        onMove(this);
    }
    onDragEnd() {
        const { editorContext: { handlers: { action: { onDrop }, }, }, } = this.props;
        this.isDragging = false;
        onDrop(this);
    }
    drawNodeHighlight() {
        const { isHighlighted, editorContext } = this.props;
        const { state: { highlightState }, } = editorContext;
        const activeHighlightType = isHighlighted
            ? highlightState.type
            : HighlightType.NONE;
        setHighlightSpriteVisibility(this.nodeHighlightTypes, activeHighlightType);
    }
    drawNodePosition() {
        const { node } = this.props;
        const { pageX, pageY } = node;
        const container = this.ref;
        container.position.set(pageX, pageY);
    }
    isChanged(props) {
        const { editorContext, isHighlighted, node: { pageX, pageY }, } = props;
        const { editorContext: propEditorContext, isHighlighted: propIsHighlighted, node: { pageX: propPageX, pageY: propPageY }, } = this.props;
        const { state: { highlightState: propHighlight }, } = propEditorContext;
        const { state: { highlightState: highlight }, } = editorContext;
        const isPositionDifferent = pageX !== propPageX || pageY !== propPageY;
        const isHighlightDifferent = isHighlighted !== propIsHighlighted &&
            /*
             * Don't redraw if highlight type is info (purple),
             * as that'd be the same color as the default color.
             */
            (highlight.type !== propHighlight.type ||
                highlight.type !== HighlightType.INFO);
        return isPositionDifferent || isHighlightDifferent;
    }
    redraw(props) {
        this.updatePropsWithoutRedraw(props);
        this.drawNodePosition();
        this.drawNodeHighlight();
    }
    updatePropsWithoutRedraw(props) {
        this.props = props;
        this.addDragListeners();
    }
    unmount() {
        const container = this.ref;
        const dragListener = this.dragListener;
        container.destroy();
        dragListener === null || dragListener === void 0 ? void 0 : dragListener.removeListeners();
    }
}
__decorate([
    boundMethod
], ActionNode.prototype, "onClick", null);
__decorate([
    boundMethod
], ActionNode.prototype, "onDragStart", null);
__decorate([
    boundMethod
], ActionNode.prototype, "onDragMove", null);
__decorate([
    boundMethod
], ActionNode.prototype, "onDragEnd", null);
